import React, { useMemo, useCallback, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import AdminTable from '@common/components/table/AdminTable';
import ApiService from '@common/services/ApiService';
import { unitResponsesStyles } from './styles';
import VcmProfessionalQualificationDetailCell from './VcmProfessionalQualificationDetail';
import VcmProfessionalQualificationCode from './VcmProfessionalQualificationCode';
import VcmUnitResponseStatus from './VcmUnitResponseStatus';
import VcmProfessionalQualificationRejectModal from './VcmProfessionalQualificationRejectModal';
import ErrorToast from './ErrorToast';
import { vcmStatusOptions } from './options';

const fields = ['professional_qualification.code', 'detail', 'status'];

const api = new ApiService();
const VcmProfessionalQualifications = ({
  vcm,
  refreshTick,
  onRefresh,
  classes,
  institute
}) => {
  const [isPending, setIsPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [vcmProfessionalQualificationForModal, setVcmProfessionalQualificationForModal] = useState(undefined);
  const [isRejectModalOpened, setIsRejectModalOpened] = useState(false);

  const openRejectModal = useCallback(
    (vcmProfessionalQualification) => {
      setVcmProfessionalQualificationForModal(vcmProfessionalQualification);
      setIsRejectModalOpened(true);
    },
    [setVcmProfessionalQualificationForModal, setIsRejectModalOpened]
  );

  const handleCloseRejectModal = useCallback(() => {
    setVcmProfessionalQualificationForModal(undefined);
    setIsRejectModalOpened(false);
  }, [setIsRejectModalOpened, setVcmProfessionalQualificationForModal]);


  const metadata = useMemo(
    () => ({
      'professional_qualification.code': {
        type: 'component',
        label: 'Unit',
        component: VcmProfessionalQualificationCode,
        tableCellProps: { className: classes.tableCell }
      },
      detail: {
        type: 'component',
        label: 'Details',
        component: VcmProfessionalQualificationDetailCell,
        componentProps: { vcm, institute },
        tableCellProps: { className: classes.tableCell }
      },
      status: {
        type: 'component',
        label: 'Status',
        component: VcmUnitResponseStatus,
        tableCellProps: { className: classes.tableCell }
      }
    }),
    [classes.tableCell, vcm, institute]
  );

  const handleApprove = useCallback(async (vcmProfessionalQualification) => {
    setIsPending(true);

    try {
      const url = `/api/v1/vcms/${vcmProfessionalQualification.vcm_id}/professional_qualifications/${vcmProfessionalQualification.professional_qualification.id}/approve`;
      const res = await api.put(url);
      if (res.status >= 200 && res.status < 400) {
        onRefresh();
      } else {
        setErrorMessage(res.json.message);
      }
    } catch (error) {
      setErrorMessage('Something went wrong, please try again');
    } finally {
      setIsPending(false);
    }
  }, []);

  const ApproveButton = ({ vcmProfessionalQualification }) => {
    return (
      <Button
        disabled={isPending || vcm.status !== vcmStatusOptions.in_review.id}
        onClick={() => {
          handleApprove(vcmProfessionalQualification);
        }}
        className={`${classes.actionButtons} ${classes.approveButton}`}
        color="secondary"
        size="small"
        variant="contained"
      >
        <Typography variant="button">Approve</Typography>
      </Button>
    );
  };

  const RejectButton = ({ vcmProfessionalQualification }) => {
    return (
      <Button
        disabled={isPending || vcm.status !== vcmStatusOptions.in_review.id}
        onClick={() => {
          openRejectModal(vcmProfessionalQualification);
        }}
        className={`${classes.actionButtons} ${classes.rejectButton}`}
        color="secondary"
        size="small"
        variant="contained"
      >
        <Typography variant="button">Mark For Attention</Typography>
      </Button>
    );
  };

  const renderButtonActions = useCallback((vcmProfessionalQualification) => {
    if (!vcmProfessionalQualification) {
      return null;
    }

    return (
      <>
        <ApproveButton vcmProfessionalQualification={vcmProfessionalQualification} />
        <RejectButton vcmProfessionalQualification={vcmProfessionalQualification} />
      </>
    );
  }, []);

  return (
    <>
      <AdminTable
        tableBottomPadding
        actionsSection={['buttons']}
        renderButtonActions={renderButtonActions}
        fields={fields}
        metadata={metadata}
        resource={`vcms/${vcm.id}/professional_qualifications`}
        actionCellProps={{
          className: classes.tableCell,
          width: 300
        }}
        refreshTick={refreshTick}
      />
      <VcmProfessionalQualificationRejectModal
        isOpened={isRejectModalOpened}
        onClose={handleCloseRejectModal}
        vcmProfessionalQualification={vcmProfessionalQualificationForModal}
        onRefresh={onRefresh}
      />
      <ErrorToast
        open={!!errorMessage}
        errorMessage={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
};

export default withStyles(VcmProfessionalQualifications, unitResponsesStyles, {
  withTheme: true
});
